// Sugalarm — Cycle 243.5: a compact, branded, keyboard-free time picker.
//
// Why: the native `<input type="time">` clock-dial picker is too tall for the
// kiosk's short landscape viewport (1024×640) — its OK/SET button clips below
// the screen, so a reminder time can't be set. This is a custom control that
// needs no native dialog and no on-screen keyboard: − / + steppers (with
// press-and-hold auto-repeat) for hour + minute, plus an AM/PM toggle in
// 12-hour mode. Tokens only; matches the threshold-stepper + segmented-control
// aesthetic. Value is the "HH:MM" 24-hour string the reminder editor stores.

import { useCallback, useRef } from "react";

import styles from "./TimePicker.module.css";

function parse(hhmm: string): { h: number; m: number } {
  const [hs, ms] = (hhmm || "10:00").split(":");
  let h = parseInt(hs ?? "", 10);
  let m = parseInt(ms ?? "", 10);
  if (!Number.isFinite(h)) h = 10;
  if (!Number.isFinite(m)) m = 0;
  return { h: ((h % 24) + 24) % 24, m: ((m % 60) + 60) % 60 };
}

function fmt(h: number, m: number): string {
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

export interface TimePickerProps {
  /** "HH:MM" 24-hour. */
  value: string;
  onChange: (value: string) => void;
  /** 24-hour display (no AM/PM). */
  h24: boolean;
}

export function TimePicker({ value, onChange, h24 }: TimePickerProps) {
  const { h, m } = parse(value);
  const isPM = h >= 12;
  const display12 = ((h + 11) % 12) + 1; // 0→12, 13→1

  const stepHour = (d: number) => onChange(fmt(((h + d) % 24 + 24) % 24, m));
  const stepMin = (d: number) => onChange(fmt(h, ((m + d) % 60 + 60) % 60));
  const setPM = (pm: boolean) => {
    if (pm === isPM) return;
    onChange(fmt((h + 12) % 24, m));
  };

  // Press-and-hold auto-repeat (coarse-then-fine), mirroring the threshold
  // stepper feel so dragging minute 0→38 isn't 38 taps.
  const timers = useRef<{ delay: number | null; rep: number | null }>({
    delay: null,
    rep: null,
  });
  const stop = useCallback(() => {
    if (timers.current.delay != null) window.clearTimeout(timers.current.delay);
    if (timers.current.rep != null) window.clearInterval(timers.current.rep);
    timers.current.delay = null;
    timers.current.rep = null;
  }, []);
  const start = useCallback(
    (fn: () => void) => {
      fn();
      timers.current.delay = window.setTimeout(() => {
        timers.current.rep = window.setInterval(fn, 90);
      }, 350);
    },
    [],
  );

  const StepBtn = ({
    sign,
    label,
    onStep,
  }: {
    sign: "−" | "+";
    label: string;
    onStep: () => void;
  }) => (
    <button
      type="button"
      className={styles.step}
      aria-label={label}
      onPointerDown={(e) => {
        e.preventDefault();
        start(onStep);
      }}
      onPointerUp={stop}
      onPointerLeave={stop}
      onPointerCancel={stop}
      onBlur={stop}
    >
      <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
        {sign === "+" ? (
          <path
            d="M8 3 L8 13 M3 8 L13 8"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        ) : (
          <path
            d="M3 8 L13 8"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        )}
      </svg>
    </button>
  );

  return (
    <div className={styles.picker}>
      <div className={styles.unit} role="group" aria-label="Hour">
        <StepBtn sign="−" label="Hour down" onStep={() => stepHour(-1)} />
        <span className={styles.num}>
          {h24 ? String(h).padStart(2, "0") : String(display12).padStart(2, "0")}
        </span>
        <StepBtn sign="+" label="Hour up" onStep={() => stepHour(1)} />
      </div>

      <span className={styles.colon}>:</span>

      <div className={styles.unit} role="group" aria-label="Minute">
        <StepBtn sign="−" label="Minute down" onStep={() => stepMin(-1)} />
        <span className={styles.num}>{String(m).padStart(2, "0")}</span>
        <StepBtn sign="+" label="Minute up" onStep={() => stepMin(1)} />
      </div>

      {!h24 ? (
        <div className={styles.ampm} role="group" aria-label="AM or PM">
          <button
            type="button"
            className={`${styles.ampmBtn} ${!isPM ? styles.ampmActive : ""}`}
            aria-pressed={!isPM}
            onClick={() => setPM(false)}
          >
            AM
          </button>
          <button
            type="button"
            className={`${styles.ampmBtn} ${isPM ? styles.ampmActive : ""}`}
            aria-pressed={isPM}
            onClick={() => setPM(true)}
          >
            PM
          </button>
        </div>
      ) : null}
    </div>
  );
}
